import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaSortUp, FaSortDown, FaDownload, FaArrowLeft } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import { fetchUserDownloadedBooks, exportUserDownloadedBooksCsv } from '../services/api';
import '../components/DataTable.css';
import './DataPages.css';
import './Pages.css';

const UserDownloadedBooks = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('DESC');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [exporting, setExporting] = useState(false);
  const pageSize = 100;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchUserDownloadedBooks(userId, page, pageSize, sortOrder, searchTerm);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, page, sortOrder, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleSortToggle = () => {
    setSortOrder(prev => prev === 'DESC' ? 'ASC' : 'DESC');
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleDownloadCsv = async () => {
    try {
      setExporting(true);
      const response = await exportUserDownloadedBooksCsv(userId, sortOrder, searchTerm);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}h${String(now.getMinutes()).padStart(2,'0')}m${String(now.getSeconds()).padStart(2,'0')}s`;
      link.download = `User_${userId}_Downloaded_Books_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleBack = () => {
    navigate('/downloads/user-downloads');
  };

  const items = data ? data.items : [];

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <button className="btn-back" onClick={handleBack} title="Back to User Downloads">
            <FaArrowLeft size={14} />
            <span>Back</span>
          </button>
          <h1>User Downloads</h1>
          <span className="page-subtitle">Books downloaded by this user</span>
        </div>
      </div>

      <div className="data-page-content">
        {error && <div className="table-empty">Error: {error}</div>}

        {!error && (
          <>
            <div className="data-page-controls">
              <div className="table-search-wrapper">
                <FaSearch className="table-search-icon" size={14} />
                <input
                  type="text"
                  className="table-search-input"
                  placeholder="Search by ID, title, or language..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                {searchInput && (
                  <button className="table-search-clear" onClick={handleSearchClear}>
                    <FaTimes size={14} />
                  </button>
                )}
                <button className="table-search-btn" onClick={handleSearch} title="Search">
                  <FaSearch size={14} />
                </button>
              </div>

              <button className="btn-primary" onClick={handleDownloadCsv} title="Download CSV" disabled={exporting}>
                <FaDownload size={14} />
                <span>{exporting ? 'Exporting ...' : 'CSV'}</span>
              </button>

              {data && (
                <div className="control-badge" style={{ marginLeft: 'auto' }}>
                  <span className="badge-label">Viewing</span>
                  <span className="badge-value">{data.totalCount.toLocaleString()}</span>
                </div>
              )}
            </div>

            {loading && <div className="table-loading">Loading ...</div>}

            {!loading && items.length > 0 && (
              <div className="data-page-table-card">
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Book ID</th>
                        <th>Book Title</th>
                        <th>Language</th>
                        <th>Published</th>
                        <th>Book Length</th>
                        <th className="sortable" onClick={handleSortToggle}>
                          Downloaded At
                          <span className="sort-indicator">
                            {sortOrder === 'ASC' ? <FaSortUp size={12} /> : <FaSortDown size={12} />}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={`${item.bookId}-${index}`}>
                          <td>{item.bookId}</td>
                          <td>{item.bookTitle || '-'}</td>
                          <td>{item.language || '-'}</td>
                          <td>{item.isPublished ? 'Yes' : 'No'}</td>
                          <td>{item.bookLength.toLocaleString()}</td>
                          <td>{formatDate(item.downloadedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={data.page}
                  totalPages={data.totalPages}
                  totalCount={data.totalCount}
                  pageSize={data.pageSize}
                  onPageChange={setPage}
                />
              </div>
            )}

            {!loading && data && items.length === 0 && (
              <div className="table-empty">
                {searchTerm
                  ? 'No results found for the current search.'
                  : 'No downloaded books found for this user.'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserDownloadedBooks;
